import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Stack,
  ListItemButton,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { WidgetItemsModal } from './WidgetItemsModal';
import { MoreItemsBadge } from './MoreItemsBadge';
import { api } from '../../../services/api';

const MAX_VISIBLE_ITEMS = 3;

interface Lead {
  _id: string;
  firstName: string;
  lastName?: string;
  email: string;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  createdAt: string;
}

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  new: 'primary',
  contacted: 'warning',
  qualified: 'success',
  converted: 'success',
  lost: 'error',
};

export const RecentLeadsWidget: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leads', 'recent'],
    queryFn: async () => {
      const response = await api.get('/leads', {
        params: { limit: 20, sortBy: 'createdAt', sortOrder: 'desc' },
      });
      return response.data;
    },
  });

  const leads: Lead[] = data?.data?.leads || [];
  const visibleLeads = leads.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = Math.max(0, leads.length - MAX_VISIBLE_ITEMS);

  const getFullName = (lead: Lead) => {
    return lead.lastName ? `${lead.firstName} ${lead.lastName}` : lead.firstName;
  };

  const getInitials = (lead: Lead) => {
    const first = lead.firstName?.charAt(0) || '';
    const last = lead.lastName?.charAt(0) || '';
    if (first && last) {
      return `${first}${last}`.toUpperCase();
    }
    return first.toUpperCase() || '?';
  };

  const handleLeadClick = (leadId: string) => {
    setModalOpen(false);
    navigate(`/leads?id=${leadId}`);
  };

  const LeadListItem = ({ lead, inModal = false }: { lead: Lead; inModal?: boolean }) => (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => handleLeadClick(lead._id)}
        sx={{
          py: inModal ? 1.5 : 0.75,
          px: inModal ? 2 : 0,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              bgcolor: 'success.light',
            }}
          >
            {getInitials(lead)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: inModal ? 200 : 120,
                }}
              >
                {getFullName(lead)}
              </Typography>
              <Chip
                label={lead.status}
                size="small"
                color={statusColors[lead.status] || 'default'}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Stack>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      py={2}
    >
      <PersonAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No leads yet
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Leads from your landing page will appear here
      </Typography>
    </Box>
  );

  return (
    <>
      <WidgetCard
        title="Recent Leads"
        icon={<PersonAddIcon color="success" />}
        isLoading={isLoading}
        error={error as Error | null}
        onRefresh={() => refetch()}
        viewAllLink="/leads"
        viewAllLabel="View All Leads"
        minHeight={180}
      >
        {leads.length === 0 ? (
          <EmptyState />
        ) : (
          <Box display="flex" flexDirection="column" height="100%">
            <List dense disablePadding sx={{ flex: 1 }}>
              {visibleLeads.map((lead) => (
                <LeadListItem key={lead._id} lead={lead} />
              ))}
            </List>
            {remainingCount > 0 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <MoreItemsBadge
                  count={remainingCount}
                  onClick={() => setModalOpen(true)}
                  color="success"
                  label={`+${remainingCount} more leads. Click to view all`}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetCard>

      <WidgetItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Recent Leads"
        icon={<PersonAddIcon color="success" />}
        totalCount={leads.length}
      >
        {leads.map((lead) => (
          <LeadListItem key={lead._id} lead={lead} inModal />
        ))}
      </WidgetItemsModal>
    </>
  );
};

export default RecentLeadsWidget;
